import React, { useState, useEffect, useContext, useRef } from 'react';
import Typography from '@mui/material/Typography';
import { Box, Button, MenuItem } from '@mui/material';
import { fetchCountryData, fetchCustomerType } from '../../api/util';
import { getAccountDetails, updateAccountDetailsApi } from '../../api/settings';
import Checkbox from '@mui/material/Checkbox';
import { StoreContext } from '../../components/Store';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import LoadingSpinner from '../../components/LoadingSpinner';
import FormField from '../../components/FormField';
import { isValidEmail } from '../../utils/validation';
import PropTypes from 'prop-types';
import ConfirmModal from './ConfirmModal';
import { is360PayStaff } from '../../utils/roles';

export default function AccountDetails({ companyId }) {
  const store = useContext(StoreContext);
  const [accountsData, setAccountsData] = useState({
    billable: false,
  });

  const isReadOnly = sessionStorage.getItem('department') === 'Haulier' || is360PayStaff();
  const [countriesData, setCountriesData] = useState([]);
  const [customerTypeData, setCustomerTypeData] = useState([]);

  const [image, setImage] = useState('');
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const handleCloseError = () => setErrorOpen(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // state for customer type change warning modal
  const [isCustomerTypeWarningOpen, setIsCustomerTypeWarningOpen] = useState(false);
  const [customerTypeWarningMessage, setCustomerTypeWarningMessage] = useState('');
  const [customerTypeChangeConfirmed, setCustomerTypeChangeConfirmed] = useState(null);

  const handleOnChange = (event) => {
    let title = event.target.name;
    let value = event.target.value;
    if (event.target.name === 'billable') {
      setAccountsData((prevData) => ({
        ...prevData,
        [title]: event.target.checked,
      }));
    } else {
      setAccountsData((prevData) => ({
        ...prevData,
        [title]: value,
      }));
    }
  };

  const handleCustomerTypeChange = (event) => {
    let warningMessage = '';

    // show warning if changing from certain customer types to others
    if ([30, 60].includes(accountsData.customerTypeId) && [10, 20].includes(parseInt(event.target.value))) {
      warningMessage = 'It is not advised to change a 360PAY customer Type to non 360PAY customer Type.';
    } else if ([10, 20].includes(accountsData.customerTypeId) && [30, 60].includes(parseInt(event.target.value))) {
      warningMessage = 'It is not advised to change a non 360PAY customer Type to a 360PAY customer Type.';
    } else if ([40, 50].includes(accountsData.customerTypeId) && [30, 60].includes(parseInt(event.target.value))) {
      warningMessage = 'You are about to change this to a 360PAY customer who uses SSO.';
    } else if ([40, 50].includes(accountsData.customerTypeId) && [10, 20].includes(parseInt(event.target.value))) {
      warningMessage = 'You are about to change this to a non 360PAY customer.';
    }

    if (warningMessage) {
      setCustomerTypeWarningMessage(warningMessage);
      setIsCustomerTypeWarningOpen(true);
      setCustomerTypeChangeConfirmed(event.target.value);
    } else {
      handleOnChange(event);
    }
  };

  const processCountryCode = (countryList, value) => {
    const usersCurrentCountry = countryList.find(({ countryCode }) => countryCode === value);
    if (usersCurrentCountry) {
      setAccountsData((prevData) => ({
        ...prevData,
        countryId: usersCurrentCountry['id'],
      }));
    }
  };

  const processLogo = (data) => {
    let binary = '';
    if (data?.logo?.data) {
      const bytes = new Uint8Array(data.logo?.data);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }

      setAccountsData((prevData) => ({
        ...prevData,
        image: `data:image/png;base64,${btoa(binary)}`,
      }));
    }
  };

  const fetchData = async () => {
    try {
      const countryList = await fetchCountryData();
      const customerTypeList = await fetchCustomerType();
      setCountriesData(countryList);
      setCustomerTypeData(customerTypeList);
      getAccountDetails(companyId || store.eoriCompany[0] || sessionStorage.getItem('companyid'))
        .then((data) => {
          store.accountDetails[1](data);
          for (const [key, value] of Object.entries(data)) {
            if (key === 'contactName') {
              sessionStorage.setItem('contactName', value);
            }
            // finding the ID of the country fetched from the DB and storing this with the account data
            if (key === 'countryCode') {
              processCountryCode(countryList, value);
            }
            if (key === 'logo') {
              processLogo(data);
            }
            setAccountsData((prevData) => ({
              ...prevData,
              [key]: value,
            }));
          }
        })
        .catch((err) => {
          store.showError(err.message);
        })
        .finally(() => setLoading(false));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (Object.keys(store.accountDetails?.[0]).length > 0) {
      for (const [key, value] of Object.entries(store.accountDetails[0])) {
        setAccountsData((prevData) => ({
          ...prevData,
          [key]: value,
        }));
      }
    }
  }, [store.accountDetails?.[0]]);

  const updateAccountDetails = async () => {
    try {
      const errors = {};

      const data = {
        companyId: accountsData.companyId || companyId,
        companyName: accountsData.company,
        streetAddress: accountsData.streetAddress,
        townCity: accountsData.town,
        billable: accountsData.billable,
        countryId: accountsData.countryId,
        postCode: accountsData.postcode,
        logo: image,
        customerTypeId: accountsData.customerTypeId,
        euEori: accountsData.euEori,
        customerReference: accountsData.customerReference,
        notifications: accountsData.notifications,
      };

      const fieldValidation = {
        companyName: ['required'],
        streetAddress: ['required'],
        townCity: ['required'],
        postCode: ['required'],
        countryId: ['required'],
        notifications: ['validMultipleEmails'],
        customerTypeId: ['required'],
      };

      Object.keys(data).forEach((field) => {
        const validations = fieldValidation[field] || [];

        validations.forEach((validation) => {
          if (validation === 'required' && (data[field] === '' || !data[field])) {
            errors[field] = '$fieldName is required.';
          } else if (validation === 'validEmail' && !isValidEmail(data[field])) {
            errors[field] = '$fieldName must be a valid email address.';
          } else if (validation === 'validMultipleEmails' && data[field]) {
            const emails = data[field].split(',').map((email) => email.trim());
            const invalidEmails = emails.filter((email) => email && !isValidEmail(email));
            if (invalidEmails.length > 0) {
              errors[field] = 'Please enter valid email addresses separated by commas.';
            }
          }
        });
      });

      setFormErrors(errors);

      if (!Object.keys(errors).length) {
        const response = await updateAccountDetailsApi(data);

        if (response?.length > 0) {
          setOpen(true);
          fetchData();
        } else if (response?.error) {
          setErrorOpen(true);
          setErrorMessage(response.error);
        }
      }
    } catch (error) {
      console.error('Error updating account details:', error);
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0'); // 26
    const month = String(date.getMonth() + 1).padStart(2, '0'); // 02 (Months are 0-based)
    const year = String(date.getFullYear()).slice(-2); // 25
    const hours = String(date.getHours()).padStart(2, '0'); // 09
    const minutes = String(date.getMinutes()).padStart(2, '0'); // 51
    const seconds = String(date.getSeconds()).padStart(2, '0'); // 25

    return `${day}/${month}/${year} - ${hours}:${minutes}:${seconds}`;
  };
  const onImageChange = (event) => {
    if (event.target.files?.[0]) {
      let img = event.target.files[0];
      setImage(img);
      setAccountsData((prevData) => ({
        ...prevData,
        ['image']: URL.createObjectURL(img),
      }));
    }
  };

  const deleteLogo = () => {
    setAccountsData((prevData) => ({
      ...prevData,
      ['image']: '',
    }));
    setImage('');
    inputRef.current.value = '';
  };

  const getCountryName = (data, countryId) => {
    const country = data.find((item) => item.id === countryId);
    return country ? country.countryName : 'n/a';
  };

  const getCustomerTypeValue = (data, typeId) => {
    const customerType = data.find((item) => item.typeId === typeId);
    return customerType ? customerType.typeName : 'n/a';
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Box className="row-wrapper account-settings">
          <FormField
            label="Company Name"
            name="company"
            onChange={handleOnChange}
            value={accountsData.company || ''}
            readOnly={isReadOnly}
            error={formErrors.companyName}
            required
          />

          <FormField label="GB EORI Number" name="eori" value={accountsData.eori || 'n/a'} readOnly={true} />

          <FormField
            label="EU EORI Number"
            name="euEori"
            onChange={handleOnChange}
            value={accountsData.euEori || ''}
            readOnly={isReadOnly}
          />

          <FormField
            label="Street Address"
            name="streetAddress"
            onChange={handleOnChange}
            value={accountsData.streetAddress || ''}
            readOnly={isReadOnly}
            error={formErrors.streetAddress}
            required
          />

          <FormField
            label="Town / City"
            name="town"
            onChange={handleOnChange}
            value={accountsData.town || ''}
            readOnly={isReadOnly}
            error={formErrors.townCity}
            required
          />

          <FormField
            label="Postcode"
            name="postcode"
            onChange={handleOnChange}
            value={accountsData.postcode || ''}
            readOnly={isReadOnly}
            error={formErrors.postCode}
            required
          />

          <FormField
            label="Country"
            name="countryId"
            onChange={handleOnChange}
            value={isReadOnly ? getCountryName(countriesData, accountsData.countryId) : accountsData.countryId}
            readOnly={isReadOnly}
            error={formErrors.countryId}
            required
            select
          >
            {countriesData.map((country) => (
              <MenuItem key={country.id} value={country.id}>
                {country.countryName}
              </MenuItem>
            ))}
          </FormField>

          <FormField
            label="Contact Name"
            name="contactName"
            value={accountsData.contactName || 'n/a'}
            readOnly={true}
          />

          <FormField
            label="Contact Email"
            name="contactEmail"
            value={accountsData.contactEmail || 'n/a'}
            readOnly={true}
          />

          <FormField
            label="Contact Phone Number"
            name="phoneNumber"
            value={accountsData.phoneNumber || 'n/a'}
            readOnly={true}
          />

          <FormField
            label="Notifications"
            name="notifications"
            onChange={handleOnChange}
            value={accountsData.notifications || ''}
            readOnly={is360PayStaff()}
            error={formErrors.notifications}
            tooltip="Enter one or more email addresses, separated by commas. These addresses will receive the responses from the declaration."
          />

          {!isReadOnly && (
            <>
              <FormField
                label="Last Modified By User"
                name="lastModifiedUser"
                value={accountsData.lastModifiedUser || 'n/a'}
                readOnly={true}
              />
              <FormField
                label="Last Modified By Date"
                name="lastModifiedUser"
                value={accountsData.lastModified ? formatDate(accountsData.lastModified) : 'n/a'}
                readOnly={true}
              />
              <Box className="col-wrapper">
                {/* Label */}
                <Typography variant="body1" className="typography">
                  Billable
                </Typography>

                <Checkbox
                  className="field-component"
                  onChange={handleOnChange}
                  name="billable"
                  checked={accountsData.billable}
                />
              </Box>

              <FormField
                label="Customer Reference"
                name="customerReference"
                onChange={handleOnChange}
                value={accountsData.customerReference || ''}
                readOnly={isReadOnly}
              />
            </>
          )}

          <FormField
            label="Customer Type"
            name="customerTypeId"
            onChange={handleCustomerTypeChange}
            value={
              isReadOnly
                ? getCustomerTypeValue(customerTypeData, accountsData.customerTypeId)
                : accountsData.customerTypeId || ''
            }
            readOnly={isReadOnly}
            error={formErrors.customerTypeId}
            required
            select
          >
            {customerTypeData.map((ct) => (
              <MenuItem key={ct.typeId} value={ct.typeId}>
                {ct.typeName}
              </MenuItem>
            ))}
          </FormField>

          {accountsData.billable && !isReadOnly && (
            <Box className="col-wrapper">
              <Typography variant="body1" className="typography">
                Logo
              </Typography>
              <div className="image-upload-container">
                <div className="image-upload">
                  <input
                    ref={inputRef}
                    id="files"
                    type="file"
                    name="myImage"
                    className="image-uploaded"
                    onChange={onImageChange}
                  />
                  {accountsData.image && <img alt="account data" src={accountsData.image} />}
                </div>
                <label htmlFor="files" className="upload" style={{ display: 'inline-block' }}>
                  Choose File
                </label>
                {accountsData.image && (
                  <>
                    {' '}
                    &nbsp;|&nbsp;{' '}
                    <button onClick={deleteLogo} className="delete-logo">
                      Delete logo
                    </button>
                  </>
                )}
              </div>
            </Box>
          )}

          {!is360PayStaff() && (
            <Box sx={{ flexGrow: 1 }} className="button-search-container">
              <Button
                style={{ marginTop: '1rem', marginBottom: '1rem' }}
                className={'button-class'}
                onClick={updateAccountDetails}
              >
                Update Details
              </Button>
            </Box>
          )}
          <Dialog
            open={open}
            onClose={handleClose}
            className="usersTable"
            PaperProps={{
              component: 'form',
              onSubmit: (event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries(formData.entries());
                const email = formJson.email;
                console.log(email);
                handleClose();
              },
            }}
          >
            <DialogContent>
              <DialogContentText>Saved successfully</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} className="button-class">
                Ok
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={errorOpen}
            onClose={handleCloseError}
            className="usersTable"
            PaperProps={{
              component: 'form',
              onSubmit: (event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries(formData.entries());
                const email = formJson.email;
                console.log(email);
                handleCloseError();
              },
            }}
          >
            <DialogContent>
              <DialogContentText>{errorMessage}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseError} className="button-class">
                Ok
              </Button>
            </DialogActions>
          </Dialog>

          <ConfirmModal
            isOpen={isCustomerTypeWarningOpen}
            heading="Changing Customer Type"
            onCancel={() => setIsCustomerTypeWarningOpen(false)}
            onConfirm={() => {
              handleOnChange({ target: { name: 'customerTypeId', value: customerTypeChangeConfirmed } });
              setIsCustomerTypeWarningOpen(false);
            }}
          >
            <p style={{ fontWeight: 'bold' }}>{customerTypeWarningMessage}</p>
            <p>Please confirm you want to continue and save changes?</p>
          </ConfirmModal>
        </Box>
      )}
    </>
  );
}

AccountDetails.propTypes = {
  companyId: PropTypes.string,
};
